import Vue from 'vue';
import Vuex, {ActionContext} from 'vuex';
import i18n from '@/plugins/i18n';

import AdminModule from '@/store/modules/Admin';
import AdvertModule from '@/store/modules/Advert';
import CategoryModule from '@/store/modules/Category';
import ConversationsModule from '@/store/modules/Conversations';
import ProfileModule from '@/store/modules/Profile';
import {AdminState} from '@/store/modules/Admin/state';
import {AdvertState} from '@/store/modules/Advert/state';
import {CategoryState} from '@/store/modules/Category/state';
import {ConversationsState} from '@/store/modules/Conversations/state';
import {ProfileState} from '@/store/modules/Profile/state';
import {AxiosResponse} from 'axios';

Vue.use(Vuex);

export type SnackBarType = 'valid' | 'error' | 'information';

export interface ContextAppBar {
    display: boolean;
    type: string;
}

export interface SnackBarOptions {
    display: boolean;
    color: string;
    text: string;
    type: SnackBarType;
}

export interface RootState {
    contextAppBar: ContextAppBar;
    online: boolean;
    pageTitle: string;
    saveSettings: boolean;
    snackBar: SnackBarOptions;
    username: string;
    AdminModule: AdminState;
    AdvertModule: AdvertState;
    CategoryModule: CategoryState;
    ConversationsModule: ConversationsState;
    ProfileModule: ProfileState;
}

export const ERROR_400 = <string>i18n.t('store.errors.400');
export const ERROR_403 = <string>i18n.t('store.errors.403');
export const ERROR_404 = <string>i18n.t('store.errors.404');
export const ERROR_500 = <string>i18n.t('store.errors.500');
export const OFFLINE_ERROR = <string>i18n.t('store.errors.offline');
export const UNDEFINED_ERROR = <string>i18n.t('store.errors.undefined');

export const uuidGenerate = (): string => {
    const uuidTemplate = 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx';
    let uuid = '';

    for (const char of uuidTemplate) {
        uuid += char === 'x' ? (Math.floor(Math.random() * 16)).toString(16) : char;
    }

    return uuid;
};

// TODO: Utiliser le profile pour avoir le username et plus le JWT

export default new Vuex.Store<RootState>({
    state: {
        contextAppBar: {
            display: false,
            type: '',
        },
        online: true,
        pageTitle: '',
        saveSettings: false,
        snackBar: {
            display: false,
            color: 'snackBarValid',
            text: 'Weather modifié',
            type: 'valid',
        },
        username: '',
    } as RootState,
    getters: {
        contextAppBar: (state: RootState): ContextAppBar => state.contextAppBar,
        isOffline: (state: RootState): boolean => (!state.online),
        pageTitle: (state: RootState): string => state.pageTitle,
        saveSettings: (state: RootState): boolean => state.saveSettings,
        snackBar: (state: RootState): SnackBarOptions => state.snackBar,
        username: (state: RootState): string => state.username,
    },
    actions: {
        /**
         * Ferme la snackbar qui affiche les informations.
         */
        closeSnackBar: ({commit}: ActionContext<RootState, RootState>): void => {
            commit('CLOSE_SNACK_BAR');
        },
        /**
         * Affiche la snackbar quand c'est une erreur. Choisi le bon message d'erreur en fonction du code status.
         */
        displayError: (
            {dispatch, state}: ActionContext<RootState, RootState>,
            error: AxiosResponse<{ status: number } | undefined>,
        ): Promise<void> => {
            return new Promise(resolve => {
                let text = '';
                if (error !== undefined) {
                    const {status} = error;

                    switch (status) {
                        case 400:
                            text = ERROR_400;
                            break;

                        case 403:
                            text = ERROR_403;
                            break;

                        case 404:
                            text = ERROR_404;
                            break;

                        case 500:
                            text = ERROR_500;
                            break;

                        default:
                            text = ERROR_500;
                            break;
                    }

                    status === 401
                        ? resolve()
                        : dispatch('displaySnackBar', {text, type: 'error'})
                            .then(() => {
                                resolve();
                            });
                }

                state.online
                    ? dispatch('displaySnackBar', {text: UNDEFINED_ERROR, type: 'error'})
                        .then(() => {
                            resolve();
                        })
                    : dispatch('displaySnackBar', {text: OFFLINE_ERROR, type: 'information'})
                        .then(() => {
                            resolve();
                        });
            });
        },
        /**
         * Ouvre la snackbar et affiche le text passé en paramètre.
         */
        displaySnackBar: (
            {commit}: ActionContext<RootState, RootState>,
            {text, type}: { text: string, type: SnackBarType },
        ): void => {
            commit('UPDATE_MESSAGE', text);
            commit('UPDATE_STATUS', type);
            commit('OPEN_SNACK_BAR');
        },
        /**
         * Récupère le username de l'utilisateur dans le JWT.
         */
        getUsernameInJWT: ({commit}: ActionContext<RootState, RootState>): void => {
            const username = window.localStorage.JWT__access__token
                ? JSON.parse(window.atob(window.localStorage.JWT__access__token.split(/\./)[1])).username
                : '';
            commit('GET_USERNAME', username);
        },
        saveDefaultSettings: ({commit}: ActionContext<RootState, RootState>, signal: boolean): void => {
            commit('SAVE_DEFAULT_SETTINGS', signal);
        },
        setAppOnlineStatus: ({commit}: ActionContext<RootState, RootState>, status: boolean): void => {
            commit('SET_ONLINE_STATUS', status);
        },
        updateAppBarContext: (
            {commit}: ActionContext<RootState, RootState>,
            {status, type}: { status: boolean, type: string },
        ): void => {
            commit('SWITCH_APP_BAR_CONTEXT_STATUS', status);
            commit('UPDATE_APP_BAR_CONTEXT_TYPE', type);
        },
        /**
         * Appelée dans toutes les pages pour afficher un titre personnalisé dans le fichier AppTemplate,
         * en fonction de la page.
         */
        updatePageTitle: (
            {commit}: ActionContext<RootState, RootState>,
            newTitle = '',
        ): void => {
            const pageTitle = newTitle.length > 80
                ? newTitle.substring(0, 80).concat('...')
                : newTitle;
            commit('UPDATE_PAGE_TITLE', pageTitle);
        },
    },
    mutations: {
        CLOSE_SNACK_BAR: (state: RootState) => {
            state.snackBar.display = false;
            state.snackBar.text = '';
        },
        GET_USERNAME: (state: RootState, username: string) => {
            state.username = username;
        },
        OPEN_SNACK_BAR: (state: RootState) => {
            state.snackBar.display = true;
        },
        SAVE_DEFAULT_SETTINGS: (state: RootState, signal: boolean) => {
            state.saveSettings = signal;
        },
        SET_ONLINE_STATUS: (state: RootState, status: boolean) => {
            state.online = status;
        },
        SWITCH_APP_BAR_CONTEXT_STATUS: (state: RootState, status: boolean) => {
            state.contextAppBar.display = status;
        },
        UPDATE_APP_BAR_CONTEXT_TYPE: (state: RootState, type: string) => {
            state.contextAppBar.type = type;
        },
        UPDATE_MESSAGE: (state: RootState, text: string) => {
            state.snackBar.text = text;
        },
        UPDATE_PAGE_TITLE: (state: RootState, newTitle: string) => {
            state.pageTitle = newTitle;
        },
        UPDATE_STATUS: (state: RootState, status: SnackBarType) => {
            state.snackBar.type = status;
            state.snackBar.color = status === 'error' ? 'snackBarError' : 'snackBarValid';
        },
    },
    modules: {
        AdminModule,
        AdvertModule,
        CategoryModule,
        ConversationsModule,
        ProfileModule,
    },
});
